import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, Lock, User, Loader2, ArrowLeft, Eye, EyeOff, CheckCircle2, Play, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const Signup = () => {
  const navigate = useNavigate();
  const { signup, googleLogin } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Please fill in email and password');
      return;
    }

    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      await signup(email, password, name);
      setSignupSuccess(true);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Success state
  if (signupSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ backgroundColor: "#F5F5F0" }} data-testid="signup-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full text-center bg-white rounded-2xl p-10"
          style={{ border: "1px solid rgba(0,0,0,0.06)", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}
        >
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-display font-semibold mb-3" style={{ color: "#1A1A1A" }}>Check your email</h1>
          <p className="mb-6" style={{ color: "rgba(26,26,26,0.5)" }}>
            We've sent a verification link to <strong>{email}</strong>. 
            Please check your inbox and click the link to verify your account.
          </p>
          <div className="space-y-3">
            <Button 
              onClick={() => navigate('/login')} 
              className="w-full rounded-full border-0"
              style={{ backgroundColor: "#FF5A36", color: "#FFFFFF" }}
              data-testid="go-to-login-btn"
            >
              Go to Login
            </Button>
            <p className="text-sm" style={{ color: "rgba(26,26,26,0.5)" }}>
              Didn't receive the email?{' '}
              <button 
                onClick={() => setSignupSuccess(false)}
                className="hover:underline font-medium"
                style={{ color: "#FF5A36" }}
              >
                Try again
              </button>
            </p>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: "#F5F5F0" }} data-testid="signup-page">
      {/* Left side - Form */}
      <div className="flex-1 flex flex-col justify-center px-8 sm:px-16 lg:px-24">
        <div className="max-w-md w-full mx-auto">
          {/* Back to home */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm transition-colors mb-8"
            style={{ color: "rgba(26,26,26,0.5)" }}
            data-testid="back-to-home-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>

          {/* Logo */}
          <div className="flex items-center gap-3 mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_2d4aff0d-e5b7-449b-860f-09baa683a8bc/artifacts/b3uq1zlx_Logo.png" 
              alt="Prysh AI" 
              className="w-10 h-10 object-contain" 
            />
            <span className="font-display text-xl font-semibold" style={{ color: "#1A1A1A" }}>Prysh AI</span>
          </div>

          {/* Title */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <h1 className="text-3xl font-display font-semibold mb-2" style={{ color: "#1A1A1A" }}>Create your account</h1>
            <p className="mb-8" style={{ color: "rgba(26,26,26,0.5)" }}>
              Start creating AI-powered video ads in minutes
            </p>
          </motion.div>

          {/* Google Sign In */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            <Button
              type="button"
              variant="outline"
              className="w-full h-12 gap-3 rounded-full bg-white border"
              style={{ borderColor: "rgba(0,0,0,0.12)", color: "#1A1A1A" }}
              onClick={googleLogin}
              data-testid="google-signup-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Continue with Google
            </Button>
          </motion.div>

          {/* Divider */}
          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full" style={{ borderTop: "1px solid rgba(0,0,0,0.08)" }}></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="px-3" style={{ backgroundColor: "#F5F5F0", color: "rgba(26,26,26,0.4)" }}>Or continue with email</span>
            </div>
          </div>

          {/* Signup Form */}
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            onSubmit={handleSubmit}
            className="space-y-5"
          >
            <div className="space-y-2">
              <Label htmlFor="name" style={{ color: "#1A1A1A" }}>Name (optional)</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "rgba(26,26,26,0.35)" }} />
                <Input
                  id="name"
                  type="text"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="pl-10 h-12 bg-white rounded-xl"
                  style={{ borderColor: "rgba(0,0,0,0.1)" }}
                  data-testid="name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" style={{ color: "#1A1A1A" }}>Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "rgba(26,26,26,0.35)" }} />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-12 bg-white rounded-xl"
                  style={{ borderColor: "rgba(0,0,0,0.1)" }}
                  data-testid="email-input"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" style={{ color: "#1A1A1A" }}>Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "rgba(26,26,26,0.35)" }} />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Min. 8 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 h-12 bg-white rounded-xl"
                  style={{ borderColor: "rgba(0,0,0,0.1)" }}
                  data-testid="password-input"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                  style={{ color: "rgba(26,26,26,0.4)" }}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs" style={{ color: "rgba(26,26,26,0.4)" }}>Must be at least 8 characters</p>
            </div>

            <Button 
              type="submit" 
              className="w-full h-12 rounded-full text-base font-medium border-0"
              style={{ backgroundColor: "#FF5A36", color: "#FFFFFF" }}
              disabled={loading}
              data-testid="signup-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating account...
                </>
              ) : (
                'Create account'
              )}
            </Button>
          </motion.form>

          {/* Login link */}
          <p className="text-center text-sm mt-8" style={{ color: "rgba(26,26,26,0.5)" }}>
            Already have an account?{' '}
            <Link 
              to="/login" 
              className="font-medium hover:underline"
              style={{ color: "#FF5A36" }}
              data-testid="login-link"
            >
              Sign in
            </Link>
          </p>

          {/* Terms */}
          <p className="text-center text-xs mt-6" style={{ color: "rgba(26,26,26,0.4)" }}>
            By creating an account, you agree to our{' '}
            <a href="#" className="underline hover:opacity-80">Terms of Service</a>
            {' '}and{' '}
            <a href="#" className="underline hover:opacity-80">Privacy Policy</a>
          </p>
        </div>
      </div>

      {/* Right side - Visual */}
      <div className="hidden lg:flex flex-1 items-center justify-center p-12 relative overflow-hidden" style={{ backgroundColor: "#1A1A1A" }}>
        {/* Subtle glow */}
        <div className="absolute top-1/4 right-1/4 w-[400px] h-[400px] rounded-full blur-[120px]" style={{ backgroundColor: "rgba(255,90,54,0.15)" }} />
        <div className="absolute bottom-1/4 left-1/4 w-[300px] h-[300px] rounded-full blur-[100px]" style={{ backgroundColor: "rgba(255,90,54,0.08)" }} />
        
        <div className="max-w-lg text-center relative z-10">
          <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-8" style={{ backgroundColor: "rgba(255,90,54,0.15)" }}>
            <Sparkles className="w-10 h-10" style={{ color: "#FF5A36" }} />
          </div>
          <h2 className="text-2xl font-display font-semibold mb-4 text-white">
            Join Prysh AI
          </h2>
          <p className="text-white/50 leading-relaxed">
            Create scroll-stopping video ads powered by AI. 
            Just paste a product URL and watch the magic happen.
          </p>
          <div className="flex items-center justify-center gap-6 mt-10">
            {["AI Video", "Smart Editing", "Auto Ads"].map((tech) => (
              <div key={tech} className="flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" style={{ color: "#FF5A36" }} />
                <span className="text-xs font-medium text-white/40">{tech}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
